import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ChevronRight, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, incomeBracketsFor } from "@/lib/constants";
import { LogoMark } from "@/components/Logo";

const STEPS = ["Account", "Basics", "Background", "Lifestyle", "Preferences"];

export default function Register() {
  const { register, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    looking_for: "Bride",
    gender: "Male",
    dob: "",
    country: "India",
    location: "",
    religion: "",
    community: "",
    mother_tongue: "",
    height_cm: 170,
    marital_status: "Never Married",
    education: "",
    profession: "",
    income: "",
    diet: "",
    drinking: "",
    smoking: "",
    about_me: "",
    partner_preferences: { age_min: 22, age_max: 32, religion: "", location: "" },
  });

  const update = (patch) => setForm({ ...form, ...patch });
  const updatePref = (patch) => setForm({ ...form, partner_preferences: { ...form.partner_preferences, ...patch } });

  const next = async () => {
    setError("");
    if (step === 0) {
      // Create account first
      if (!form.name || !form.email || !form.password) {
        setError("Please fill all account fields");
        return;
      }
      setLoading(true);
      try {
        await register({
          name: form.name,
          email: form.email,
          password: form.password,
          looking_for: form.looking_for,
          gender: form.gender,
        });
        setStep(1);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Registration failed");
      } finally {
        setLoading(false);
      }
      return;
    }
    // Other steps: PATCH profile
    setLoading(true);
    try {
      const patch = stepPayload(step);
      await api.put("/profile/me", patch);
      if (step < STEPS.length - 1) {
        setStep(step + 1);
      } else {
        await refresh();
        toast.success("Profile complete! Welcome to Right Match.");
        navigate("/dashboard");
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setLoading(false);
    }
  };

  const stepPayload = (s) => {
    if (s === 1) return { dob: form.dob, country: form.country, location: form.location, height_cm: Number(form.height_cm), marital_status: form.marital_status };
    if (s === 2) return { religion: form.religion, community: form.community, mother_tongue: form.mother_tongue, education: form.education, profession: form.profession, income: form.income };
    if (s === 3) return { diet: form.diet, drinking: form.drinking, smoking: form.smoking, about_me: form.about_me };
    if (s === 4) return { partner_preferences: form.partner_preferences };
    return {};
  };

  const back = () => setStep(Math.max(0, step - 1));

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="register-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <LogoMark size="text-2xl" />
        </Link>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((label, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? "bg-brand-maroon" : "bg-brand-line"}`} />
              <p className={`text-xs mt-2 ${i === step ? "text-brand-maroon font-medium" : "text-brand-inkMute"}`}>{label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-brand-line rounded-lg p-8 md:p-10 shadow-sm">
          <p className="overline">Step {step + 1} of {STEPS.length}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-brand-ink mt-2">
            {step === 0 && "Create your account"}
            {step === 1 && "Tell us about you"}
            {step === 2 && "Your background"}
            {step === 3 && "Your lifestyle"}
            {step === 4 && "Your partner preferences"}
          </h1>

          <div className="mt-8 space-y-5">
            {step === 0 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline">I'm looking for</label>
                    <select value={form.looking_for} onChange={(e) => update({ looking_for: e.target.value, gender: e.target.value === "Bride" ? "Male" : "Female" })} className="input-elegant mt-1" data-testid="reg-looking-for">
                      <option>Bride</option><option>Groom</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">My gender</label>
                    <select value={form.gender} onChange={(e) => update({ gender: e.target.value })} className="input-elegant mt-1" data-testid="reg-gender">
                      <option>Male</option><option>Female</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="overline">Full name</label>
                  <input value={form.name} onChange={(e) => update({ name: e.target.value })} className="input-elegant mt-1" data-testid="reg-name" />
                </div>
                <div>
                  <label className="overline">Email</label>
                  <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} className="input-elegant mt-1" data-testid="reg-email" />
                </div>
                <div>
                  <label className="overline">Password</label>
                  <input type="password" value={form.password} onChange={(e) => update({ password: e.target.value })} className="input-elegant mt-1" data-testid="reg-password" />
                  <p className="text-xs text-brand-inkMute mt-1">At least 6 characters</p>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div>
                  <label className="overline">Date of birth</label>
                  <input type="date" value={form.dob} onChange={(e) => update({ dob: e.target.value })} className="input-elegant mt-1" data-testid="reg-dob" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline">Country</label>
                    <select value={form.country} onChange={(e) => update({ country: e.target.value })} className="input-elegant mt-1" data-testid="reg-country">
                      {COUNTRIES.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline">City</label>
                    <input value={form.location} onChange={(e) => update({ location: e.target.value })} placeholder="e.g. Toronto, Karachi, London..." className="input-elegant mt-1" data-testid="reg-location" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline">Height (cm)</label>
                    <input type="number" value={form.height_cm} onChange={(e) => update({ height_cm: e.target.value })} min="120" max="220" className="input-elegant mt-1" data-testid="reg-height" />
                  </div>
                  <div>
                    <label className="overline">Marital status</label>
                    <select value={form.marital_status} onChange={(e) => update({ marital_status: e.target.value })} className="input-elegant mt-1" data-testid="reg-marital">
                      <option>Never Married</option><option>Divorced</option><option>Widowed</option><option>Awaiting Divorce</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline">Religion</label>
                    <select value={form.religion} onChange={(e) => update({ religion: e.target.value })} className="input-elegant mt-1" data-testid="reg-religion">
                      <option value="">Select</option>
                      <option>Hindu</option><option>Muslim</option><option>Christian</option>
                      <option>Sikh</option><option>Jain</option><option>Buddhist</option><option>Parsi</option><option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Community / Caste</label>
                    <input value={form.community} onChange={(e) => update({ community: e.target.value })} className="input-elegant mt-1" data-testid="reg-community" />
                  </div>
                </div>
                <div>
                  <label className="overline">Mother tongue</label>
                  <input value={form.mother_tongue} onChange={(e) => update({ mother_tongue: e.target.value })} placeholder="Hindi, Tamil, Marathi..." className="input-elegant mt-1" data-testid="reg-mother-tongue" />
                </div>
                <div>
                  <label className="overline">Highest education</label>
                  <input value={form.education} onChange={(e) => update({ education: e.target.value })} placeholder="B.Tech, MBA, MBBS..." className="input-elegant mt-1" data-testid="reg-education" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline">Profession</label>
                    <input value={form.profession} onChange={(e) => update({ profession: e.target.value })} className="input-elegant mt-1" data-testid="reg-profession" />
                  </div>
                  <div>
                    <label className="overline">Annual income</label>
                    <select value={form.income} onChange={(e) => update({ income: e.target.value })} className="input-elegant mt-1" data-testid="reg-income">
                      <option value="">Select</option>
                      {incomeBracketsFor(form.country).map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="overline">Diet</label>
                    <select value={form.diet} onChange={(e) => update({ diet: e.target.value })} className="input-elegant mt-1" data-testid="reg-diet">
                      <option value="">Select</option>
                      <option>Vegetarian</option><option>Non-Vegetarian</option><option>Eggetarian</option><option>Vegan</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Drinking</label>
                    <select value={form.drinking} onChange={(e) => update({ drinking: e.target.value })} className="input-elegant mt-1" data-testid="reg-drinking">
                      <option value="">Select</option>
                      <option>Never</option><option>Socially</option><option>Often</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Smoking</label>
                    <select value={form.smoking} onChange={(e) => update({ smoking: e.target.value })} className="input-elegant mt-1" data-testid="reg-smoking">
                      <option value="">Select</option>
                      <option>Never</option><option>Socially</option><option>Often</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="overline">About me</label>
                  <textarea rows={5} value={form.about_me} onChange={(e) => update({ about_me: e.target.value })} placeholder="Share a bit about yourself, your values, your hopes..." className="input-elegant mt-1" data-testid="reg-about" />
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <p className="text-sm text-brand-inkLight">Help us find the right matches for you.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline">Age min</label>
                    <input type="number" value={form.partner_preferences.age_min} onChange={(e) => updatePref({ age_min: Number(e.target.value) })} min="18" max="80" className="input-elegant mt-1" data-testid="reg-pref-age-min" />
                  </div>
                  <div>
                    <label className="overline">Age max</label>
                    <input type="number" value={form.partner_preferences.age_max} onChange={(e) => updatePref({ age_max: Number(e.target.value) })} min="18" max="80" className="input-elegant mt-1" data-testid="reg-pref-age-max" />
                  </div>
                </div>
                <div>
                  <label className="overline">Preferred religion</label>
                  <input value={form.partner_preferences.religion} onChange={(e) => updatePref({ religion: e.target.value })} placeholder="Any" className="input-elegant mt-1" data-testid="reg-pref-religion" />
                </div>
                <div>
                  <label className="overline">Preferred location</label>
                  <input value={form.partner_preferences.location} onChange={(e) => updatePref({ location: e.target.value })} placeholder="Any city or region" className="input-elegant mt-1" data-testid="reg-pref-location" />
                </div>
              </>
            )}
          </div>

          {error && <p className="text-sm text-brand-maroon mt-4">{error}</p>}

          <div className="flex items-center justify-between mt-10">
            <button onClick={back} disabled={step === 0 || loading} className="text-sm text-brand-inkLight hover:text-brand-maroon flex items-center gap-1 disabled:opacity-30" data-testid="reg-back">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button onClick={next} disabled={loading} className="btn-primary flex items-center gap-2" data-testid="reg-next">
              {loading ? "Saving..." : step === STEPS.length - 1 ? <>Finish <Check className="h-4 w-4" /></> : <>Continue <ChevronRight className="h-4 w-4" /></>}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-brand-inkMute mt-6">Already have an account? <Link to="/login" className="text-brand-maroon hover:underline">Sign in</Link></p>
      </div>
    </div>
  );
}
